import { useEffect, useRef, useState } from 'react'

interface UseCountUpOptions {
  durationMs?: number
}

const DEFAULT_DURATION_MS = 800

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

// Fast start, settles at the end — reads as "counting up and landing", not linear ticking.
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

/**
 * Animates a numeric value counting up from 0 to `target` the first time it's used. A
 * later `target` change (e.g. a background refetch updating an already-displayed KPI)
 * snaps directly to the new value instead of re-animating, so live data updates don't
 * replay the intro animation every time.
 */
export function useCountUp(target: number, options: UseCountUpOptions = {}): number {
  const durationMs = options.durationMs ?? DEFAULT_DURATION_MS
  const [value, setValue] = useState(() => (prefersReducedMotion() ? target : 0))
  const hasAnimatedRef = useRef(false)

  useEffect(() => {
    if (hasAnimatedRef.current || prefersReducedMotion()) {
      hasAnimatedRef.current = true
      setValue(target)
      return
    }

    let frame: number
    const start = performance.now()

    function tick(now: number) {
      const progress = Math.min((now - start) / durationMs, 1)
      setValue(target * easeOutCubic(progress))
      if (progress < 1) {
        frame = requestAnimationFrame(tick)
      } else {
        hasAnimatedRef.current = true
      }
    }

    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [target, durationMs])

  return value
}
